import { z } from "zod";

import type { Parser } from "../identity/live/schemas";
import type {
  Analysed,
  Analysis,
  CrsInfo,
  Fit,
  ImportReply,
  ParseReply,
  SiteRecord,
  SiteView,
  TraverseReply,
} from "./types";

/** The site routes' JSON, checked before pages see it. The service sends `null` for what TypeScript leaves out. */

function maybe<T extends z.ZodType>(schema: T) {
  return schema.nullish().transform((value) => value ?? undefined);
}

const latLon = z.object({ lat: z.number(), lon: z.number() });

const crs = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("wgs84") }),
  z.object({ kind: z.literal("utm"), zone: z.number(), south: z.boolean() }),
  z.object({ kind: z.literal("local"), origin: latLon }),
]);

const geoReference = z.object({
  crs,
  surveyPoint: z.object({ easting: z.number(), northing: z.number(), elevation: z.number() }),
  northAngleDeg: z.number(),
  verticalDatum: z.enum(["egm2008", "egm96", "local"]),
  scaleFactor: z.number(),
});

const matchType = z.enum(["rooftop", "street", "locality", "pinCentroid"]);

const vertex = z.object({ x: z.number(), y: z.number(), bulge: maybe(z.number()) });
const boundary = z.object({ vertices: z.array(vertex) });

const identifiers = z.object({
  surveyNumber: maybe(z.string()),
  hissa: maybe(z.string()),
  cts: maybe(z.string()),
  khasra: maybe(z.string()),
  fpTp: maybe(z.string()),
  ulpin: maybe(z.string()),
});

export const recordSchema = z.object({
  location: maybe(
    z.object({
      point: latLon,
      source: z.enum(["pin", "coordinates", "mapsLink", "plusCode", "search"]),
      label: maybe(z.string()),
      matchType: maybe(matchType),
      timeZone: maybe(z.string()),
    }),
  ),
  geoReference: maybe(geoReference),
  plots: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      identifiers,
      boundary: maybe(boundary),
    }),
  ),
});

const edge = z.object({
  from: z.number(),
  to: z.number(),
  lengthM: z.number(),
  azimuthDeg: z.number(),
  quadrantBearing: z.string(),
  radiusM: maybe(z.number()),
  sweepDeg: maybe(z.number()),
});

const analysis = z.object({
  areaM2: z.number(),
  areaSqFt: z.number(),
  regionalAreas: z.array(z.object({ unit: z.string(), value: z.number() })),
  perimeterM: z.number(),
  edges: z.array(edge),
  counterClockwise: z.boolean(),
});

const problem = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("tooFewVertices") }),
  z.object({ kind: z.literal("duplicateVertex"), index: z.number() }),
  z.object({
    kind: z.literal("selfIntersection"),
    a: z.number(),
    b: z.number(),
    at: z.tuple([z.number(), z.number()]),
  }),
  z.object({ kind: z.literal("notFinite") }),
]);

const analysed = z.object({ boundary, analysis: maybe(analysis), problems: z.array(problem) });

const timeZone = z.object({
  zone: z.string(),
  abbreviation: z.string(),
  offsetMinutes: z.number(),
  approximate: z.boolean(),
});

const norths = z.object({
  projectFromTrueDeg: z.number(),
  convergenceDeg: z.number(),
  gridFromTrueDeg: z.number(),
});

const viewSchema = z.object({
  record: recordSchema,
  version: z.number(),
  updatedBy: maybe(z.string()),
  updatedAt: maybe(z.string()),
  derived: z.object({
    plots: z.array(z.object({ id: z.string(), analysis: maybe(analysis) })),
    timeZone: maybe(timeZone),
    norths: maybe(norths),
  }),
});

const parseReplySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("located"),
    point: latLon,
    format: z.enum(["decimal", "dms", "mapsLink", "plusCode"]),
    timeZone,
  }),
  z.object({
    kind: z.literal("candidates"),
    provider: z.string(),
    attribution: maybe(z.string()),
    candidates: z.array(
      z.object({
        label: z.string(),
        point: latLon,
        matchType,
        confidence: z.number(),
        needsPin: z.boolean(),
      }),
    ),
  }),
  z.object({ kind: z.literal("searchOff"), message: z.string() }),
]);

const traverseSchema = z.object({
  traverse: z.object({
    misclosureM: z.number(),
    misclosureEastM: z.number(),
    misclosureNorthM: z.number(),
    precisionRatio: maybe(z.number()),
    precisionText: z.string(),
    perimeterM: z.number(),
    adjusted: z.boolean(),
  }),
  analysis: analysed,
});

const importSchema = z.object({
  plots: z.array(
    analysed.extend({
      name: z.string(),
      origin: maybe(latLon),
      centre: maybe(z.tuple([z.number(), z.number()])),
      layer: maybe(z.string()),
      chainedFromLines: z.boolean(),
    }),
  ),
  layers: z.array(z.string()),
  metresPerUnit: maybe(z.number()),
  unitsDeclared: maybe(z.boolean()),
});

const fitSchema = z.object({
  rmsM: z.number(),
  residualsM: z.array(z.number()),
  flagged: z.array(z.number()),
  transform: z.object({
    m: z.tuple([z.tuple([z.number(), z.number()]), z.tuple([z.number(), z.number()])]),
    t: z.tuple([z.number(), z.number()]),
  }),
});

export const parseSiteView: Parser<SiteView> = { parse: (value) => viewSchema.parse(value) };
export const parseParseReply: Parser<ParseReply> = {
  parse: (value) => parseReplySchema.parse(value),
};
export const parseAnalysed: Parser<Analysed> = { parse: (value) => analysed.parse(value) };
export const parseTraverse: Parser<TraverseReply> = {
  parse: (value) => traverseSchema.parse(value),
};
export const parseImport: Parser<ImportReply> = { parse: (value) => importSchema.parse(value) };
export const parseFit: Parser<Fit> = { parse: (value) => fitSchema.parse(value) };
export const parseCrsList: Parser<CrsInfo[]> = {
  parse: (value) => z.array(z.object({ epsg: z.number(), name: z.string() })).parse(value),
};
export const parseNorth: Parser<{ northAngleDeg: number }> = {
  parse: (value) => z.object({ northAngleDeg: z.number() }).parse(value),
};
export type { Analysis, SiteRecord };
